/**
 * One `disabled` property, several controllers with
 * an opinion about it.
 *
 * The checkout's Place order buttons are held shut for
 * unrelated reasons by unrelated controllers:
 * `courier-quote` while there is no usable quote,
 * `tip-selector` while the basket sits under the
 * online-payment floor. Each one assigning
 * `button.disabled` directly means the last writer
 * wins, and the customer gets a live CTA that the
 * server then refuses: a filled-in checkout ejected
 * back to the cart.
 *
 * So no controller sets `disabled`. Each records its
 * own REASON on the button and the property is
 * recomputed as the OR of every reason present. A
 * controller releasing its own hold cannot free a
 * button somebody else is still holding, and a third
 * reason later adds a key and changes nothing else.
 *
 * The `Blocked` suffix is the whole contract: no
 * controller needs to know another's reason name.
 */
const BLOCK_SUFFIX = "Blocked";

export function setSubmitBlock(button, reason, blocked) {
  if (!button) return;

  if (blocked) {
    button.dataset[reason] = "true";
  } else {
    delete button.dataset[reason];
  }

  button.disabled = Object.keys(button.dataset).some(
    (key) => key.endsWith(BLOCK_SUFFIX)
  );

  // Kept in step with the property, not merely set
  // alongside it. `disabled` is what assistive tech
  // actually reads on a native button, so this is
  // belt-and-braces, but a stale `aria-disabled="true"`
  // on a button that has since gone live announces the
  // opposite of the truth, which is worse than the
  // attribute never being there. Rendered by the
  // server, maintained here.
  button.setAttribute(
    "aria-disabled",
    String(button.disabled)
  );
}

/**
 * Every submit the checkout offers: the sticky
 * footer, the summary panel, the order-details sheet.
 * Read off `form.elements` rather than queried under
 * the form, because two of the three render OUTSIDE it
 * and are reconnected with the `form` attribute:
 * `querySelector` is scoped to descendants and cannot
 * see them.
 */
/**
 * A submit button that is NOT the order CTA, and so
 * must never be held by a quote or a payment floor.
 *
 * `form.elements` is defined to include every
 * form-associated control wherever it sits, which is
 * what lets this file reach a CTA rendered outside the
 * controller's element. The admin order builder uses
 * the same property in the other direction: its item
 * tiles and line steppers are submit buttons on the
 * order form, so that a tap posts the discount the
 * merchant has typed rather than a blank copy of it.
 * Without this exemption a courier quote with no
 * address yet would disable every one of them, and a
 * merchant on a delivery order could not add a single
 * item to it (CAL-499).
 *
 * Opt IN, so every existing button is untouched.
 */
const EXEMPT_ATTRIBUTE = "data-submit-block-exempt";

export function submitButtonsFor(form) {
  if (!form) return [];

  return Array.from(form.elements).filter(
    (element) =>
      element.type === "submit" &&
      !element.hasAttribute(EXEMPT_ATTRIBUTE)
  );
}

/**
 * What the CTA says: "Place order" and the amount, or
 * the reason it cannot be pressed yet.
 *
 * Lives here because two controllers need to say it
 * and neither owns the answer. `courier-quote` swaps
 * it as an address resolves into a quote;
 * `address-autocomplete` swaps it on a flat-fee
 * delivery, where no quote exists and the address
 * alone releases the button. Two copies of this would
 * drift the first time the markup changed.
 *
 * `text` REPLACES the reason, for the case where the
 * button is held continuously but for a new reason:
 * a customer supplies the address the button asked
 * for and is then waiting on a price. Leaving the
 * previous sentence up would have the button asking
 * for something it already has. Always a string the
 * SERVER translated and handed over as a value;
 * nothing here builds copy.
 *
 * A checkout that renders no prompt span (every type
 * but delivery) is left alone.
 */
export function setSubmitPrompt(form, prompting, text) {
  submitButtonsFor(form).forEach((button) => {
    const label = button.querySelector(
      '[data-checkout-submit="label"]'
    );
    const prompt = button.querySelector(
      '[data-checkout-submit="prompt"]'
    );
    if (!label || !prompt) return;

    if (text) prompt.textContent = text;

    label.classList.toggle("hidden", prompting);
    prompt.classList.toggle("hidden", !prompting);

    const amount = button.querySelector(
      '[data-checkout-submit="amount"]'
    );
    if (amount) {
      amount.classList.toggle("hidden", prompting);
    }
  });
}
